// Data conversion system for spreadsheet fields
// Based on Java implementation from SystemField.java and FHQ4ServiceImpl.java

export interface DataConverter {
  actualValue: string | number;
  displayValue: string;
}

// Field IDs from Java SystemField.java
export const FIELD_IDS = {
  SEX: 2,
  GENDER: 148,
  DECEASED: 6,
  TWIN_STATUS: 19,
  TWIN_RELATIONSHIP: 20,
  DEGREE_3: 34,
  PROBAND_STATUS: 5,
  ADOPTED: 11,
  NO_ISSUE: 14,
  SAB_STATUS: 12,
  GENDER_UNKNOWN: 10,
  AUDIT_STATUS: 30,
  SAMPLE_AUDIT_STATUS: 31,
  INVITE_STATUS: 88,
  GENDER_IDENTITY: 149,
  LOCK_STATUS: 150
} as const;

// Date formatting constants
export const DATE_FORMAT_CONSTANTS = {
INPUT_FORMAT: 'MM/DD/YYYY',  // Format for date input fields
DISPLAY_FORMAT: 'MM/DD/YYYY', // Format for display
API_FORMAT: 'YYYY-MM-DD',     // Format for API calls
} as const;

// Gender field constants - based on Java mapGenderIntToString and mapGenderStringToInt
export const GENDER_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Woman' },
  { actualValue: '1', displayValue: 'Man' },
  { actualValue: '2', displayValue: 'Unknown' },
  { actualValue: '3', displayValue: 'Transgender woman' },
  { actualValue: '4', displayValue: 'Transgender man' },
  { actualValue: '5', displayValue: 'Non-binary' },
  { actualValue: '6', displayValue: 'Self-described' },
];

// Sex field constants - based on Java mapSexIntToString and mapSexStringToInt
export const SEX_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Female' },
  { actualValue: '1', displayValue: 'Male' },
  { actualValue: '2', displayValue: 'Unknown' },
];

// Sex query dropdown values match GWT SystemField.getDropdownValues(..., false): U, M, F
export const SEX_QUERY_CONSTANTS: DataConverter[] = [
  { actualValue: 'U', displayValue: 'U' },
  { actualValue: 'M', displayValue: 'M' },
  { actualValue: 'F', displayValue: 'F' },
];

// Deceased field constants - based on Java mapDeceasedIntToString and mapDeceasedStringToInt
export const DECEASED_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Alive' },
  { actualValue: '1', displayValue: 'Deceased' },
];

// Twin relationship constants - based on Java mapTwinRelIntToString and mapTwinRelStringToInt
export const TWIN_RELATIONSHIP_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Dizygotic' },
  { actualValue: '1', displayValue: 'Monozygotic' },
  { actualValue: '2', displayValue: 'Unknown' },
];

// Degree 3 constants - based on Java mapDegree3IntToString and mapDegree3StringToInt
export const DEGREE_3_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'None' },
  { actualValue: '1', displayValue: 'Mat/Maternal' },
  { actualValue: '2', displayValue: 'Pat/Paternal' },
  { actualValue: '3', displayValue: 'Both' },
  { actualValue: '4', displayValue: 'Con/Consanguineous' },
];

// Yes/No constants for boolean fields
export const YES_NO_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'No' },
  { actualValue: '1', displayValue: 'Yes' },
  { actualValue: '2', displayValue: 'Unknown' },
];

// Yes/No constants for boolean fields
export const ADOPTED_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'No' },
  { actualValue: '1', displayValue: 'In' },
  { actualValue: '2', displayValue: 'Out' },
];

// Yes/No constants for boolean fields
export const NO_ISSUE_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'No' },
  { actualValue: '1', displayValue: 'Yes' },
  { actualValue: '2', displayValue: 'Infertile' },
];

// Off/On constants for boolean fields
export const AUDIT_STATUS_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Off' },
  { actualValue: '1', displayValue: 'On' },
];

// Map of field IDs to their constants (for system fields)
export const FIELD_ID_CONSTANTS_MAP: Record<number, DataConverter[]> = {
  [FIELD_IDS.SEX]: SEX_CONSTANTS,
  [FIELD_IDS.GENDER]: GENDER_CONSTANTS,
  [FIELD_IDS.GENDER_IDENTITY]: GENDER_CONSTANTS,
  [FIELD_IDS.DECEASED]: DECEASED_CONSTANTS,
  [FIELD_IDS.TWIN_RELATIONSHIP]: TWIN_RELATIONSHIP_CONSTANTS,
  [FIELD_IDS.DEGREE_3]: DEGREE_3_CONSTANTS,
  [FIELD_IDS.PROBAND_STATUS]: YES_NO_CONSTANTS,
  [FIELD_IDS.ADOPTED]: ADOPTED_CONSTANTS,
  [FIELD_IDS.NO_ISSUE]: NO_ISSUE_CONSTANTS,
  [FIELD_IDS.SAB_STATUS]: YES_NO_CONSTANTS,
  [FIELD_IDS.GENDER_UNKNOWN]: YES_NO_CONSTANTS,
  [FIELD_IDS.TWIN_STATUS]: YES_NO_CONSTANTS,
  [FIELD_IDS.AUDIT_STATUS]: AUDIT_STATUS_CONSTANTS,
  [FIELD_IDS.SAMPLE_AUDIT_STATUS]: AUDIT_STATUS_CONSTANTS,
  [FIELD_IDS.INVITE_STATUS]: YES_NO_CONSTANTS,
  [FIELD_IDS.LOCK_STATUS]: YES_NO_CONSTANTS,
};

// Helper function to get constants for a field by ID
export const getFieldConstantsById = (fieldId: number): DataConverter[] | undefined => {
  return FIELD_ID_CONSTANTS_MAP[fieldId];
};

export const getQueryFieldConstantsById = (fieldId: number): DataConverter[] | undefined => {
  if (fieldId === FIELD_IDS.SEX) {
    return SEX_QUERY_CONSTANTS;
  }
  return getFieldConstantsById(fieldId);
};

export const normalizeSexQueryStoredValue = (value: string): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return "";
  }

  const upper = trimmed.toUpperCase();
  if (upper === "F" || trimmed.toLowerCase() === "female") {
    return "F";
  }
  if (upper === "M" || trimmed.toLowerCase() === "male") {
    return "M";
  }
  if (upper === "U" || trimmed.toLowerCase() === "unknown") {
    return "U";
  }
  if (trimmed === "0") {
    return "F";
  }
  if (trimmed === "1") {
    return "M";
  }
  if (trimmed === "2") {
    return "U";
  }

  return trimmed;
};

export const normalizeSystemQueryStoredValue = (fieldId: number, value: string): string => {
  if (fieldId === FIELD_IDS.SEX) {
    return normalizeSexQueryStoredValue(value);
  }

  const constants = getQueryFieldConstantsById(fieldId);
  if (!constants?.length) {
    return String(value ?? "").trim();
  }

  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return "";
  }

  const byActual = constants.find((option) => String(option.actualValue) === trimmed);
  if (byActual) {
    return String(byActual.actualValue);
  }

  const byDisplay = constants.find(
    (option) => String(option.displayValue).toLowerCase() === trimmed.toLowerCase()
  );
  if (byDisplay) {
    return String(byDisplay.actualValue);
  }

  return trimmed;
};

export const MATERNAL_PATERNAL_FIELD_ID = FIELD_IDS.DEGREE_3;

type FieldWithIdAndOptions = {
  id: number;
  system?: boolean;
  options?: Array<{ actualValue?: string | number; displayValue?: string | number; value?: string | number; label?: string | number }>;
  dropdownValues?: Array<string | number>;
};

export function withMaternalPaternalDropdownOptions<T extends FieldWithIdAndOptions>(field: T): T {
  if (field.id !== MATERNAL_PATERNAL_FIELD_ID) {
    return field;
  }
  if (field.options && field.options.length > 0) {
    return field;
  }
  const opts = DEGREE_3_CONSTANTS.map((o) => ({
    actualValue: String(o.actualValue),
    displayValue: String(o.displayValue),
  }));
  return { ...field, options: opts };
}

export function withSystemFieldDropdownOptions<T extends FieldWithIdAndOptions>(field: T): T {
  const queryConstants = getQueryFieldConstantsById(field.id);

  if (queryConstants?.length) {
    return {
      ...field,
      options: queryConstants.map((option) => ({
        actualValue: String(option.actualValue),
        displayValue: String(option.displayValue),
      })),
    };
  }

  if (field.options && field.options.length > 0) {
    return field;
  }

  return field;
}

const resolveOptionDisplayValue = (
  fieldId: number,
  actualValue: string,
  displayValue: string
): string => {
  if (displayValue && displayValue !== actualValue) {
    return displayValue;
  }

  const match = getFieldConstantsById(fieldId)?.find(
    (option) => String(option.actualValue) === actualValue
  );
  return match ? String(match.displayValue) : displayValue || actualValue;
};

export const mapQueryDropdownOptions = (
  field: FieldWithIdAndOptions
): Array<{ actualValue: string; displayValue: string }> => {
  const enrichedField = withSystemFieldDropdownOptions(withMaternalPaternalDropdownOptions(field));
  const queryConstants = getQueryFieldConstantsById(enrichedField.id);

  if (queryConstants?.length) {
    return queryConstants.map((option) => ({
      actualValue: String(option.actualValue),
      displayValue: String(option.displayValue),
    }));
  }

  const sourceOptions = enrichedField.options || [];
  if (sourceOptions.length) {
    return sourceOptions.map((fieldOpt) => {
      const actualValue = String(fieldOpt.actualValue ?? fieldOpt.value ?? "");
      const rawDisplay = String(fieldOpt.displayValue ?? fieldOpt.label ?? actualValue);
      return {
        actualValue,
        displayValue: resolveOptionDisplayValue(enrichedField.id, actualValue, rawDisplay),
      };
    });
  }

  const dropdownValues = enrichedField.dropdownValues;
  if (dropdownValues?.length) {
    return dropdownValues.map((value) => {
      const actualValue = String(value);
      return {
        actualValue,
        displayValue: resolveOptionDisplayValue(enrichedField.id, actualValue, actualValue),
      };
    });
  }

  return [];
};

// Helper function to convert raw value to display value (for UI display)
export const convertValueToDisplay = (fieldId: number, rawValue: string | number): string => {
  const constants = getFieldConstantsById(fieldId);
  if (!constants) return String(rawValue);

  const constant = constants.find(c => c.actualValue === String(rawValue));
  return constant?.displayValue || String(rawValue);
};

// Helper function to convert display value to raw value (for API payloads)
export const convertDisplayToValue = (fieldId: number, displayValue: string): string | number => {
  const constants = getFieldConstantsById(fieldId);
  if (!constants) return displayValue;

  const constant = constants.find(c => c.displayValue === displayValue);
  return constant?.actualValue || displayValue;
};

// Date conversion functions
export const formatDateForInput = (dateValue: string | number | Date): string => {
if (!dateValue) return '';

try {
  // Accept multiple input formats including 2025/10/08
  const INPUT_FORMATS = [
    'YYYY/MM/DD', 'MM/DD/YYYY', 'DD/MM/YYYY',
    'YYYY-MM-DD', 'MM-DD-YYYY', 'DD-MM-YYYY'
  ];
  // Normalize to moment first
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const momentLib = require('moment');
  let m = momentLib.invalid();

  if (typeof dateValue === 'string') {
      // String parsing - parse in UTC to avoid timezone shifts
    m = momentLib.utc(dateValue, INPUT_FORMATS, true);
      if (!m.isValid()) {
        // Fallback: try parsing as UTC date string
        m = momentLib.utc(dateValue);
      }
    } else if (dateValue instanceof Date) {
      // Date object - extract date components using UTC to avoid timezone shifts
      // This ensures the same date is displayed regardless of user's timezone
      m = momentLib.utc([dateValue.getUTCFullYear(), dateValue.getUTCMonth(), dateValue.getUTCDate()]);
    } else if (typeof dateValue === 'number') {
      // Number (timestamp) - parse as UTC to avoid timezone conversion
      m = momentLib.utc(dateValue);
  }

  if (!m.isValid()) return String(dateValue);
    // Format using UTC to ensure consistent date display across timezones
  return m.format('MM/DD/YYYY');
} catch (error) {
  console.warn('Failed to format date:', dateValue, error);
  return String(dateValue);
}
};

export const formatDateForDisplay = (dateValue: string | number | Date): string => {
return formatDateForInput(dateValue); // Same format for display
};

export const formatDateForApi = (dateValue: string): string => {
if (!dateValue) return '';

try {
  const INPUT_FORMATS = [
    'MM/DD/YYYY', 'YYYY/MM/DD', 'DD/MM/YYYY',
    'YYYY-MM-DD', 'MM-DD-YYYY', 'DD-MM-YYYY'
  ];
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const momentLib = require('moment');
  let m = momentLib(dateValue, INPUT_FORMATS, true);
  if (!m.isValid()) m = momentLib(dateValue);
  return m.isValid() ? m.format('YYYY-MM-DD') : dateValue;
} catch (error) {
  console.warn('Failed to format date for API:', dateValue, error);
  return dateValue;
}
};

// Main data converter class (similar to Java approach)
export class DataConverter {
/**
 * Convert raw data to display format for UI
 * @param fieldId - The field ID from FIELD_IDS
 * @param rawValue - The raw value from API/database
 * @returns Display value for UI
 */
static toDisplay(fieldId: number, rawValue: string | number): string {
  return convertValueToDisplay(fieldId, rawValue);
}

/**
 * Convert display value back to raw format for API payloads
 * @param fieldId - The field ID from FIELD_IDS
 * @param displayValue - The display value from UI
 * @returns Raw value for API
 */
static toApi(fieldId: number, displayValue: string): string | number {
  return convertDisplayToValue(fieldId, displayValue);
}

/**
 * Convert by field ID (for system fields)
 * @param fieldId - The field ID from FIELD_IDS
 * @param rawValue - The raw value from API/database
 * @returns Display value for UI
 */
static toDisplayById(fieldId: number, rawValue: string | number): string {
  const constants = getFieldConstantsById(fieldId);
  if (!constants) return String(rawValue);

  const constant = constants.find((c) => c.actualValue === String(rawValue));
  return constant?.displayValue || String(rawValue);
}

/**
 * Convert by field ID (for system fields)
 * @param fieldId - The field ID from FIELD_IDS
 * @param displayValue - The display value from UI
 * @returns Raw value for API
 */
static toApiById(fieldId: number, displayValue: string): string | number {
  const constants = getFieldConstantsById(fieldId);
  if (!constants) return displayValue;

  const constant = constants.find((c) => c.displayValue === displayValue);
  return constant?.actualValue || displayValue;
}

/**
 * Convert date value to MM/DD/YYYY format for input fields
 * @param dateValue - The date value (timestamp, string, or Date object)
 * @returns Formatted date string MM/DD/YYYY
 */
static formatDateForInput(dateValue: string | number | Date): string {
  return formatDateForInput(dateValue);
}

/**
 * Convert date value to MM/DD/YYYY format for display
 * @param dateValue - The date value (timestamp, string, or Date object)
 * @returns Formatted date string MM/DD/YYYY
 */
static formatDateForDisplay(dateValue: string | number | Date): string {
  return formatDateForDisplay(dateValue);
}

/**
 * Convert MM/DD/YYYY date string to YYYY-MM-DD for API calls
 * @param dateValue - The date string in MM/DD/YYYY format
 * @returns Formatted date string YYYY-MM-DD
 */
static formatDateForApi(dateValue: string): string {
  return formatDateForApi(dateValue);
}

/**
 * Convert an entire row of data for display
 * @param row - The data row
 * @param columns - The column definitions
 * @returns Row with converted display values
 */
//@ts-ignore  //WILL BE FIXED LATER
static convertRowForDisplay(row: any, columns: any[]): any {
  const convertedRow = { ...row };

  columns.forEach((column) => {
    const key = column.accessorKey || column.colName;
    if (column.system && column.id && key && row[key]) {
      convertedRow[key] = this.toDisplay(column.id, row[key]);
    }
  });

  return convertedRow;
}

/**
 * Convert an entire row of data for API payload
 * @param row - The data row with display values
 * @param columns - The column definitions
 * @returns Row with converted raw values for API
 */
//@ts-ignore  //WILL BE FIXED LATER
static convertRowForApi(row: any, columns: any[]): any {
  const convertedRow = { ...row };

  columns.forEach((column) => {
    const key = column.accessorKey || column.colName;
    if (column.system && column.id && key && row[key]) {
      convertedRow[key] = this.toApi(column.id, row[key]);
    }
  });

  return convertedRow;
}

/**
 * Get dropdown options for a field (for filter dropdowns)
 * @param fieldId - The field ID
 * @returns Array of options with actualValue and displayValue
 */
static getDropdownOptions(fieldId: number): DataConverter[] | undefined {
  return getFieldConstantsById(fieldId);
}

/**
 * Get dropdown options for a field by ID (for system fields)
 * @param fieldId - The field ID
 * @returns Array of options with actualValue and displayValue
 */
static getDropdownOptionsById(fieldId: number): DataConverter[] | undefined {
  return getFieldConstantsById(fieldId);
}

/**
 * Check if a field has conversion constants
 * @param fieldId - The field ID
 * @returns True if field has conversion constants
 */
static hasConversion(fieldId: number): boolean {
  return !!getFieldConstantsById(fieldId);
}

/**
 * Check if a field ID has conversion constants
 * @param fieldId - The field ID
 * @returns True if field has conversion constants
 */
static hasConversionById(fieldId: number): boolean {
  return !!getFieldConstantsById(fieldId);
}
}

/**
* Helper function to get field type name based on type ID
*/
export const getFieldTypeName = (typeId: number): string => {
  const typeMap: { [key: number]: string } = {
      1: 'Text',
      2: 'Date',
      3: 'Number',
      4: 'Checkbox',
      5: 'Dropdown',
      6: 'Image',
      7: 'LookupTable',
      8: 'Table',
      9: 'Computed',
      11: 'Summary',
      13: 'TableSummary',
      14: 'Hyperlink',
      30: 'Integer',
      [-100]: 'System'
  };

  return typeMap[typeId] || 'Unknown';
};
export const BLANK_DROPDOWN_LABEL = "\u00A0";
const prependBlankRowToLookupValues = (
  valuesRecord: Record<string, any[]>
): Record<string, any[]> => {
  const columnKeys = Object.keys(valuesRecord || {});
  if (columnKeys.length === 0) {
    return valuesRecord;
  }

  const alreadyHasBlank = columnKeys.every((key) => {
    const column = valuesRecord[key];
    if (!Array.isArray(column) || column.length === 0) {
      return true;
    }
    const firstValue = column[0];
    return firstValue === "" || firstValue === undefined || firstValue === null;
  });

  if (alreadyHasBlank) {
    return valuesRecord;
  }

  return columnKeys.reduce((acc, key) => {
    const columnValues = Array.isArray(valuesRecord[key])
    ? [...valuesRecord[key]!]
    : [];
    acc[key] = ["", ...columnValues];
    return acc;
  }, {} as Record<string, any[]>);
};

/**
 * Prepends a blank option (value/label empty string) to dropdown options
 * unless an empty entry already exists.
 */
export const addBlankOptionToDropdownOptions = <
  T extends { value: string | number; label: string | number }
>(
  options: T[] = []
): T[] => {
  const normalized = options ?? [];

  const hasBlank = normalized.some(
    (opt) => opt.value === "" || opt.value === null || opt.value === undefined
  );

  if (hasBlank) {
    return normalized as T[];
  }

  return [{ value: "", label: BLANK_DROPDOWN_LABEL } as T, ...normalized];
};

export const normalizeLookupValues = (lookupValues: any) => {
  if (!lookupValues) {
    return {};
  }

  // Case 1: Already in object format — clone and ensure blank row
  if (!Array.isArray(lookupValues)) {
    const normalizedRecord = Object.keys(lookupValues).reduce(
      (acc, key) => {
        const columnValues = Array.isArray(lookupValues[key])
          ? [...lookupValues[key]]
          : [];
        acc[key] = columnValues;
        return acc;
      },
      {} as Record<string, any[]>
    );
    return prependBlankRowToLookupValues(normalizedRecord);
  }

  // Case 2: Array format — convert to object then ensure blank row
  const record = lookupValues.reduce((acc, item) => {
    const columnValues = Array.isArray(item.values) ? [...item.values] : [];
    acc[item.key] = columnValues;
    return acc;
  }, {} as Record<string, any[]>);

  return prependBlankRowToLookupValues(record);
};

const removeClientOnlySpreadsheetFieldProps = (fieldPayload: any) => {
  if (!fieldPayload || typeof fieldPayload !== 'object') return;
  delete fieldPayload.name;
  delete fieldPayload.fieldName;
  delete fieldPayload.id;
  delete fieldPayload.internalId;
  delete fieldPayload.fullPath;
};

/**
 * Makes a deep copy of smartColumns and removes unwanted fields
 * (dropdown/lookup values + modulo) before sending to API.
 *
 * Keeps original data untouched.
 *
 * @param columns - smartColumns array
 * @returns cleaned smartColumns array for API
 */
export const cleanSmartColumnsForApi = (columns: any[]): any[] => {
  if (!Array.isArray(columns)) {
    return [];
  }

  return columns.map((col: any) => {
    // clone object + field + parentField to avoid mutating original
    const cloned: any = {
      ...col,
      field: col.field ? { ...col.field } : undefined,
      parentField: col.parentField ? { ...col.parentField } : undefined,
    };

    if (cloned.field) {
      delete cloned.field.dropdownValues;
      delete cloned.field.lookupValues;
      delete cloned.field.options;
      delete cloned.field.dropdownOptions;
      delete cloned.field.editable;
      // Convert id to fieldid, then remove id
      if (cloned.field.id !== undefined && cloned.field.fieldid === undefined) {
        cloned.field.fieldid = cloned.field.id;
      }
      delete cloned.field.id;
      removeClientOnlySpreadsheetFieldProps(cloned.field);
    }

    if (cloned.parentField) {
      delete cloned.parentField.dropdownValues;
      delete cloned.parentField.lookupValues;
      delete cloned.parentField.options;
      delete cloned.parentField.dropdownOptions;
      delete cloned.parentField.editable;
      // Convert id to fieldid, then remove id
      if (cloned.parentField.id !== undefined && cloned.parentField.fieldid === undefined) {
        cloned.parentField.fieldid = cloned.parentField.id;
      }
      delete cloned.parentField.id;
      delete cloned.parentField.internalId;
      delete cloned.parentField.fullPath;
      delete cloned.parentField.context;
      delete cloned.parentField.name;
      delete cloned.parentField.parentField;  // nested parentfield inside parentfield
      removeClientOnlySpreadsheetFieldProps(cloned.parentField);
    }

    // remove any column-level dropdown caches
    delete cloned.dropdownOptions;
    delete cloned.dataDropdownOptions;
    // delete cloned.editable;
    //
    // remove modulo and return rest
    const { modulo, ...rest } = cloned;
    return rest;
  });
};

/**
 * Enriches queryModel's whereClauses with dropdown/options/lookup values from smartColumns.
 * Matches fields by fieldid and optionally parentField to handle duplicate fieldids.
 * Creates a new object without mutating the original.
 */
export const hydrateQueryModelFromSmartColumns = (
  queryModel: any,
  smartColumns: any[]
): any => {
  if (!queryModel || typeof queryModel !== 'object') {
    return queryModel || { whereClauses: [] };
  }

  if (!Array.isArray(smartColumns) || smartColumns.length === 0) {
    return queryModel;
  }

  // Create a map of smartColumns by fieldid for quick lookup
  // Since fieldid can be same, we also consider parentField for matching
  const smartColumnMap = new Map<string, any>();
  
  smartColumns.forEach((smartCol: any) => {
    if (!smartCol?.field?.fieldid) return;
    
    const fieldId = smartCol.field.fieldid;
    const parentFieldId = smartCol.parentField?.fieldid || smartCol.parentField?.fieldid || 0;
    const key = `${fieldId}_${parentFieldId}`;
    
    // Store the smartColumn with the most complete field data
    if (!smartColumnMap.has(key) || smartCol.field.dropdownValues || smartCol.field.lookupValues || smartCol.field.options) {
      smartColumnMap.set(key, smartCol);
    }
  });

  // Create enriched queryModel
  const enriched: any = {
    ...queryModel,
    whereClauses: Array.isArray(queryModel.whereClauses)
      ? queryModel.whereClauses.map((clause: any) => {
          // Clone clause + field + parentField to avoid mutating original
          const enrichedClause: any = {
            ...clause,
            field: clause.field ? { ...clause.field } : undefined,
            parentField: clause.parentField ? { ...clause.parentField } : undefined,
          };

          // Enrich field with dropdown/options/lookup and core metadata from smartColumns
          if (enrichedClause.field && enrichedClause.field.fieldid) {
            const clauseFieldId = enrichedClause.field.fieldid;
            const clauseParentFieldId = enrichedClause.parentField?.fieldid || enrichedClause.parentField?.fieldId || 0;
            const key = `${clauseFieldId}_${clauseParentFieldId}`;
            
            const matchingSmartCol = smartColumnMap.get(key);
            
            // Try to find by fieldid only if no match with parentField
            let smartCol = matchingSmartCol;
            if (!smartCol) {
              // Fallback: find first smartColumn with matching fieldid (ignore parentField)
              smartCol = smartColumns.find(
                (col: any) => col?.field?.fieldid === clauseFieldId
              );
            }

            if (smartCol?.field) {
              if (!enrichedClause.parentField && smartCol.parentField) {
                const parentField = smartCol.parentField;
                const parentFieldId = parentField.fieldid ?? parentField.fieldId;
                if (parentFieldId != null) {
                  enrichedClause.parentField = {
                    fieldid: parentFieldId,
                    fieldname: parentField.fieldname || parentField.fieldName || parentField.name,
                    fieldcontext: parentField.fieldcontext || smartCol.field.fieldcontext || 'IDS_CONTEXT',
                    system: parentField.system ?? false,
                    type: parentField.fieldtype?.typeid ?? parentField.type,
                  };
                }
              }
              // Core metadata (only fill if missing on clause)
              if (!enrichedClause.field.fieldname && (smartCol.field.fieldname || smartCol.field.name)) {
                enrichedClause.field.fieldname = smartCol.field.fieldname || smartCol.field.name;
              }
              if (enrichedClause.field.type === undefined && (smartCol.field.fieldtype?.typeid !== undefined || smartCol.field.type !== undefined)) {
                enrichedClause.field.type = smartCol.field.fieldtype?.typeid ?? smartCol.field.type;
              }
              if (enrichedClause.field.system === undefined && smartCol.field.system !== undefined) {
                enrichedClause.field.system = smartCol.field.system;
              }
              if (!enrichedClause.field.imageURL && smartCol.field.imageURL) {
                enrichedClause.field.imageURL = smartCol.field.imageURL;
              }

              // Enrich with dropdown values
              if (smartCol.field.dropdownValues && !enrichedClause.field.dropdownValues) {
                enrichedClause.field.dropdownValues = [...smartCol.field.dropdownValues];
              }

              // Enrich with options
              if (smartCol.field.options && !enrichedClause.field.options) {
                enrichedClause.field.options = Array.isArray(smartCol.field.options)
                  ? [...smartCol.field.options]
                  : smartCol.field.options;
              }

              // Enrich with lookup values
              if (smartCol.field.lookupValues && !enrichedClause.field.lookupValues) {
                enrichedClause.field.lookupValues = typeof smartCol.field.lookupValues === 'object'
                  ? { ...smartCol.field.lookupValues }
                  : smartCol.field.lookupValues;
              }
            }
          }

          // Enrich parentField with dropdown/options/lookup and core metadata from smartColumns
          if (enrichedClause.parentField) {
            const parentFieldId = enrichedClause.parentField.fieldid || enrichedClause.parentField.fieldId;
            if (parentFieldId) {
              const clauseFieldId = enrichedClause.field?.fieldid || 0;
              
              // First try to find exact match: field matches AND parentField matches
              let smartCol = smartColumnMap.get(`${clauseFieldId}_${parentFieldId}`);
              
              // If exact match found but its parentField doesn't match, look for another
              if (!smartCol || smartCol.parentField?.fieldid !== parentFieldId) {
                // Find smartColumn where both field and parentField match
                smartCol = smartColumns.find(
                  (col: any) => 
                    col?.field?.fieldid === clauseFieldId &&
                    col?.parentField?.fieldid === parentFieldId
                );
              }

              // If still no match, find any smartColumn with matching parentField
              if (!smartCol || smartCol.parentField?.fieldid !== parentFieldId) {
                smartCol = smartColumns.find(
                  (col: any) => col?.parentField?.fieldid === parentFieldId
                );
              }

              if (smartCol?.parentField && smartCol.parentField.fieldid === parentFieldId) {
                // Core metadata for parentField
                if (!enrichedClause.parentField.fieldname && (smartCol.parentField.fieldname || smartCol.parentField.fieldName)) {
                  enrichedClause.parentField.fieldname = smartCol.parentField.fieldname || smartCol.parentField.fieldName;
                }
                if (enrichedClause.parentField.type === undefined && (smartCol.parentField.fieldtype?.typeid !== undefined || smartCol.parentField.type !== undefined)) {
                  enrichedClause.parentField.type = smartCol.parentField.fieldtype?.typeid ?? smartCol.parentField.type;
                }
                if (enrichedClause.parentField.system === undefined && smartCol.parentField.system !== undefined) {
                  enrichedClause.parentField.system = smartCol.parentField.system;
                }
                if (!enrichedClause.parentField.imageURL && smartCol.parentField.imageURL) {
                  enrichedClause.parentField.imageURL = smartCol.parentField.imageURL;
                }

                // Enrich parentField with dropdown values
                if (smartCol.parentField.dropdownValues && !enrichedClause.parentField.dropdownValues) {
                  enrichedClause.parentField.dropdownValues = [...smartCol.parentField.dropdownValues];
                }

                // Enrich parentField with options
                if (smartCol.parentField.options && !enrichedClause.parentField.options) {
                  enrichedClause.parentField.options = Array.isArray(smartCol.parentField.options)
                    ? [...smartCol.parentField.options]
                    : smartCol.parentField.options;
                }

                // Enrich parentField with lookup values
                if (smartCol.parentField.lookupValues && !enrichedClause.parentField.lookupValues) {
                  enrichedClause.parentField.lookupValues = typeof smartCol.parentField.lookupValues === 'object'
                    ? { ...smartCol.parentField.lookupValues }
                    : smartCol.parentField.lookupValues;
                }
              }
            }
          }

          return enrichedClause;
        })
      : [],
  };

  return enriched;
};

const issNormalizeWhereClauseValue = (
  field: { fieldid?: number; id?: number; fieldId?: number } | undefined,
  rawValue: string
): string => {
  const fieldIdRaw = field?.fieldid ?? field?.fieldId ?? field?.id;
  if (fieldIdRaw == null) {
    return rawValue;
  }
  const fieldId = Number(fieldIdRaw);
  if (Number.isNaN(fieldId)) {
    return rawValue;
  }

  const mapCommaSeparated = (value: string, mapSegment: (segment: string) => string): string =>
    value.includes(",") ? value.split(",").map((segment) => mapSegment(segment.trim())).join(",") : mapSegment(value.trim());

  if (fieldId === FIELD_IDS.SEX) {
    const numericToMfu: Record<string, string> = { "0": "F", "1": "M", "2": "U" };
    return mapCommaSeparated(rawValue, (segment) => numericToMfu[segment] ?? segment);
  }

  if (fieldId === FIELD_IDS.GENDER) {
    if (/^\d+\s*,/.test(rawValue.trim())) {
      return rawValue;
    }
    const options = FIELD_ID_CONSTANTS_MAP[FIELD_IDS.GENDER];
    return mapCommaSeparated(rawValue, (segment) => {
      const match = options?.find((option) => String(option.actualValue) === segment);
      return match ? String(match.displayValue) : segment;
    });
  }

  return rawValue;
};

export type CleanQueryModelForApiOptions = {
  normalizeIssWhereClauseStrings?: boolean;
};

export const cleanQueryModelForApi = (
  queryModel: any,
  options?: CleanQueryModelForApiOptions
): any => {
  if (!queryModel || typeof queryModel !== 'object') {
    return queryModel || { whereClauses: [] };
  }

  const normalizeIssStrings = options?.normalizeIssWhereClauseStrings === true;

  const cleaned: any = {
    ...queryModel,
    whereClauses: Array.isArray(queryModel.whereClauses) 
      ? queryModel.whereClauses.map((clause: any) => {
          const cleanedClause: any = { ...clause };
          
          if (clause.field) {
            cleanedClause.field = { ...clause.field };
            if (cleanedClause.field.fieldid === undefined && cleanedClause.field.id !== undefined) {
              cleanedClause.field.fieldid = cleanedClause.field.id;
            }
            delete cleanedClause.field.dropdownValues;
            delete cleanedClause.field.options;
            delete cleanedClause.field.lookupValues;
            removeClientOnlySpreadsheetFieldProps(cleanedClause.field);
          }

          if (clause.parentField) {
            cleanedClause.parentField = { ...clause.parentField };
            if (cleanedClause.parentField.fieldid === undefined && cleanedClause.parentField.id !== undefined) {
              cleanedClause.parentField.fieldid = cleanedClause.parentField.id;
            }
            delete cleanedClause.parentField.dropdownValues;
            delete cleanedClause.parentField.options;
            delete cleanedClause.parentField.lookupValues;
            removeClientOnlySpreadsheetFieldProps(cleanedClause.parentField);
          }

          if (normalizeIssStrings && cleanedClause.value !== undefined && cleanedClause.value !== null) {
            const clauseValue = typeof cleanedClause.value === "string" ? cleanedClause.value : String(cleanedClause.value);
            cleanedClause.value = issNormalizeWhereClauseValue(cleanedClause.field, clauseValue);
          }
          
          return cleanedClause;
        })
      : [],
  };
  
  return cleaned;
};

export const buildLoadIssQueryModelPayload = (queryModel: any, smartColumns: any[], options?: CleanQueryModelForApiOptions): any => {
  const model = queryModel && typeof queryModel === 'object' ? queryModel : { whereClauses: [] };
  return cleanQueryModelForApi(hydrateQueryModelFromSmartColumns(model, smartColumns), options);
};

export const buildLoadIssColumnFilterPayload = (filter: any, smartColumns: any[], options?: CleanQueryModelForApiOptions): any => {
  const clauses = filter && typeof filter === 'object' ? filter : { whereClauses: [] };
  if (!Array.isArray(clauses.whereClauses) || clauses.whereClauses.length === 0) {
    return cleanQueryModelForApi(clauses, options);
  }
  const queryModel = hydrateQueryModelFromSmartColumns({ whereClauses: clauses.whereClauses }, smartColumns);
  return cleanQueryModelForApi({ ...clauses, whereClauses: queryModel.whereClauses }, options);
};

// Export the main converter for easy access
export default DataConverter;